import { create } from 'zustand';
import { DEFAULT_REGION, getRegion, type RegionCode, type RegionConfig } from '@nako/core';
import { useUiStore } from './ui';

const LS_KEY = 'np.region';

/**
 * Multi-region store.
 *
 * The selected region drives currency formatting, VAT/PAYE math, the
 * BURS↔SARS↔FIRS tax-pack naming, and which language options the user is
 * offered alongside English. Persisted to localStorage so the choice
 * survives a refresh — and so the Africa-map landing page only nags
 * first-time visitors.
 */

interface RegionState {
  code: RegionCode;
  config: RegionConfig;
  /** True once the user has explicitly picked a region (vs. accepting the BW default). */
  hasChosen: boolean;
  setRegion: (code: RegionCode) => void;
}

function loadInitial(): { code: RegionCode; hasChosen: boolean } {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return { code: DEFAULT_REGION, hasChosen: false };
    const parsed = JSON.parse(raw) as { code?: string; hasChosen?: boolean };
    const cfg = getRegion(parsed.code);
    return { code: cfg.code, hasChosen: Boolean(parsed.hasChosen) };
  } catch {
    return { code: DEFAULT_REGION, hasChosen: false };
  }
}

const initial = loadInitial();

export const useRegionStore = create<RegionState>((set) => ({
  code: initial.code,
  config: getRegion(initial.code),
  hasChosen: initial.hasChosen,
  setRegion: (code) => {
    const cfg = getRegion(code);
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ code: cfg.code, hasChosen: true }));
    } catch {
      // private-mode browsers — fine, just don't persist
    }
    set({ code: cfg.code, config: cfg, hasChosen: true });
    // Reset to English if the user was in another region's local language
    // (e.g. switching BW→ZA while in Setswana mode — Setswana isn't a ZA
    // language, so drop back to English rather than keep a mismatched one).
    const currentLang = useUiStore.getState().lang;
    const primaryLocal = cfg.languages.find((l) => l.code !== 'en');
    const allLocalCodes = ['tn', 'zu', 'ha', 'pcm', 'sn'];
    if (primaryLocal && allLocalCodes.includes(currentLang) && currentLang !== primaryLocal.code) {
      useUiStore.getState().setLang('en');
    }
  },
}));

/** Convenience hook: just the currency symbol (for headings, KPI cards). */
export function useCurrencySymbol(): string {
  return useRegionStore((s) => s.config.currencySymbol);
}

/** Convenience hook: the active region config object. */
export function useRegionConfig(): RegionConfig {
  return useRegionStore((s) => s.config);
}
